import { useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment, ReactElement, useMemo } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { validationSchemaFilter } from 'front-office/constants/lots-management-schema';
import { LotsManagementCrumbs } from 'front-office/constants/lots-management-crumbs';
import {
  LotsManagementVM,
  TableLotsManagementVM,
} from 'front-office/view-models/lots-management/list';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import Filter from 'front-office/components/lots-management/filter';
import TableLotsManagement from 'front-office/components/lots-management/table';
import { HttpResponseList } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { ResponseLotsManagement } from 'shell/models/lots-management';
import { transformerLotsManagementDataList } from 'front-office/utils/transformer-lots-management';
import ListHeader from 'shell/components/shared/list-header';
import { useNavigate } from 'react-router-dom';

const LotsManagementList = (): ReactElement => {
  const { userProfile } = useUserProfile();

  const navigate = useNavigate();

  const [list, getList] = useHttpFetch<
    HttpResponseList<ResponseLotsManagement>,
    TableLotsManagementVM
  >(`${__PERSON_API_URL__}/persona/lotto-carte/gestione-lotti-cid`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformerLotsManagementDataList,
  });

  const initialValues: LotsManagementVM = useMemo(() => {
    if (userProfile.isLoading) {
      return {
        filter: { service: '', operator: '', dateFrom: null, dateTo: null },
      };
    }

    const controlloDiCassa = userProfile.datiLogin?.profile?.feature?.controlloDiCassa || '';

    const isDisabledService = controlloDiCassa === '0' || controlloDiCassa === '1';
    const isDisabledOperator = controlloDiCassa === '0';

    return {
      filter: {
        dateFrom: null,
        dateTo: null,
        service: isDisabledService
          ? userProfile.datiLogin?.profile?.servizio?.key?.codiceServizio
          : undefined,
        operator: isDisabledOperator ? userProfile.datiLogin?.key?.userName : undefined,
        entity: userProfile.datiLogin?.profile?.servizio?.key?.codiceEnte || undefined,
        isDisabledOperator,
        isDisabledService,
      },
    };
  }, [userProfile.datiLogin]);

  const onSubmitFilter = useEventCallback((values: LotsManagementVM) => {
    const data = {
      operatore: values?.filter?.operator || '',
      servizio: values?.filter?.service || '',
      assegnazioneDal: values?.filter?.dateFrom?.toFormat('yyyyMMdd') || '',
      assegnazioneAl: values?.filter?.dateTo?.toFormat('yyyyMMdd') || '',
    };

    getList({ data });
  });

  const canAdd = useMemo(() => !initialValues.filter?.isDisabledOperator, [initialValues]); // si abilita se controlloDiCassa !== '0'

  return (
    <Fragment>
      <Breadcrumbs items={LotsManagementCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo lotto"
        addBtnLabel="Aggiungi nuovo lotto"
        canAdd={canAdd}
        onAddBtnClick={() => {
          navigate('/sportello/gestione-lotti/nuovo');
        }}
        title="Gestione lotti carte d'identità"
      />
      <Formik
        validationSchema={validationSchemaFilter}
        initialValues={initialValues}
        onSubmit={onSubmitFilter}
      >
        <Form>
          <Filter />
          {list ? (
            <TableLotsManagement valuesList={list.data as TableLotsManagementVM} />
          ) : undefined}
        </Form>
      </Formik>
    </Fragment>
  );
};

LotsManagementList.displayName = 'Lots Management List Page component';

export default LotsManagementList;
